import { useId, useState } from "react";
import { AppLink } from "../components/AppLink";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormStatusMessage } from "../components/FormStatusMessage";
import { Honeypot } from "../components/Honeypot";
import { Icon } from "../components/Icon";
import { Picture } from "../components/Picture";
import { ArticleCard } from "../components/journal/ArticleCard";
import {
  articles,
  articleImageUrl,
  formatDate,
  type ArticleCategory,
} from "../data/articles";
import { useFormSubmit } from "../lib/forms";
import { Seo, breadcrumbLd } from "../lib/seo";
import { SITE, absoluteUrl } from "../lib/site";
import { btnPrimary, container } from "../lib/ui";

const ALL_CATEGORIES: ArticleCategory[] = [
  "Spatial Curation",
  "Joinery & Millwork",
  "Minimalist Theory",
  "Material Studies",
  "Lighting Design",
];
/** Only offer categories that actually have essays. */
const CATEGORIES = ALL_CATEGORIES.filter((c) =>
  articles.some((a) => a.category === c),
);

const lead = articles.find((a) => a.lead) ?? articles[0];
const rest = articles.filter((a) => a !== lead);
const latest = articles.reduce(
  (m, a) => (a.date > m.date ? a : m),
  articles[0],
);

const DESCRIPTION =
  "Interior design ideas and guides from our Nagercoil studio: lighting, joinery, natural stone, acoustics and living with humidity in South Indian homes.";

const TRAIL: Array<[string, string]> = [["Journal", "/journal"]];

const JSON_LD = [
  breadcrumbLd(TRAIL),
  {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${absoluteUrl("/journal")}#blog`,
    name: "The Atelier Journal",
    description: DESCRIPTION,
    url: absoluteUrl("/journal"),
    inLanguage: "en-IN",
    publisher: { "@id": `${SITE.origin}/#business` },
    blogPost: articles.map((a) => ({
      "@type": "BlogPosting",
      headline: a.title,
      description: a.excerpt,
      url: absoluteUrl(`/journal/${a.slug}`),
      image: articleImageUrl(a),
      datePublished: a.date,
      dateModified: a.updated ?? a.date,
      articleSection: a.category,
      author: { "@type": "Person", name: a.author.name },
    })),
  },
];

export default function Journal() {
  const [category, setCategory] = useState<"all" | ArticleCategory>("all");
  const [query, setQuery] = useState("");
  const searchId = useId();

  const q = query.trim().toLowerCase();
  const filtering = category !== "all" || q !== "";
  // Unfiltered, the lead essay is featured above the grid; once filtering, search every essay.
  const visible = (filtering ? articles : rest).filter(
    (a) =>
      (category === "all" || a.category === category) &&
      (!q ||
        [a.title, a.excerpt, a.category, a.topic].some((s) =>
          s.toLowerCase().includes(q),
        )),
  );

  const clear = () => {
    setCategory("all");
    setQuery("");
  };

  return (
    <>
      <Seo
        title="Interior Design Journal & Ideas"
        description={DESCRIPTION}
        keywords={[
          "interior design blog for Indian homes",
          "home interior ideas for humid climates",
          "interior design tips from Nagercoil designers",
          "natural stone and joinery guides for homeowners",
          "Kerala and Tamil Nadu home design ideas",
          "lighting and acoustics advice for open plan homes",
        ]}
        path="/journal"
        jsonLd={JSON_LD}
      />

      <div className="flex flex-col w-full bg-surface">
        {/* EDITORIAL HEADER */}
        <div className="relative w-full overflow-hidden">
          <div className="absolute -top-40 right-10 w-96 h-96 rounded-full bg-gold-light/20 blur-3xl pointer-events-none" />
          <div className="absolute top-20 left-1/4 w-80 h-80 rounded-full bg-emerald-vivid/10 blur-3xl pointer-events-none" />

          <section className={`relative ${container} pt-10 md:pt-12 pb-8`}>
            <Breadcrumbs trail={TRAIL} className="mb-space-md" />
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-space-lg">
              <div className="max-w-3xl">
                <h1 className="flex flex-col gap-space-sm animate-rise">
                  <span className="flex items-center gap-space-sm">
                    <span
                      className="w-8 h-px bg-secondary"
                      aria-hidden="true"
                    />
                    <span className="font-label-sm text-label-sm uppercase tracking-widest text-secondary">
                      Interior Design Journal
                    </span>
                  </span>
                  <span className="font-headline-xl text-headline-xl-mobile md:text-headline-xl text-primary tracking-tight">
                    The Atelier Journal
                  </span>
                </h1>
                <p className="font-body-xl text-body-lg md:text-body-xl text-on-surface-variant mt-space-sm font-light leading-relaxed max-w-2xl animate-rise [animation-delay:80ms]">
                  Perspectives on modern living, artisanal craft, and
                  intentional space. Curated dispatches from our Nagercoil
                  architectural atelier.
                </p>
              </div>

              <dl className="flex items-center gap-space-lg bg-surface-cream/80 backdrop-blur-md px-space-md py-space-sm rounded-lg shadow-sm self-start lg:self-auto">
                <div className="flex flex-col-reverse">
                  <dt className="font-label-sm text-label-sm uppercase text-outline">
                    Published Essays
                  </dt>
                  <dd className="font-headline-sm text-headline-sm text-primary font-serif">
                    {articles.length} Essays
                  </dd>
                </div>
                <div className="w-px h-8 bg-surface-dim" aria-hidden="true" />
                <div className="flex flex-col-reverse">
                  <dt className="font-label-sm text-label-sm uppercase text-outline">
                    Latest Essay
                  </dt>
                  <dd className="font-headline-sm text-headline-sm text-secondary font-serif">
                    <time dateTime={latest.date}>
                      {formatDate(latest.date)}
                    </time>
                  </dd>
                </div>
              </dl>
            </div>
          </section>
        </div>

        {/* LEAD ESSAY */}
        <section
          className={`${container} w-full pb-16`}
          aria-labelledby="lead-essay-title"
        >
          <article className="group relative rounded-xl overflow-hidden bg-botanical-dark shadow-xl text-surface-ivory transition-all duration-700 hover:shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 lg:min-h-[580px]">
              <div className="lg:col-span-7 relative h-72 sm:h-80 lg:h-auto overflow-hidden">
                <Picture
                  name={lead.image}
                  alt={lead.imageAlt}
                  sizes="(min-width: 1024px) 58vw, 100vw"
                  priority
                  className="absolute inset-0 block w-full h-full"
                  imgClassName="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-botanical-dark via-botanical-dark/40 to-transparent" />
                <div className="absolute top-6 left-6 flex items-center gap-space-xs bg-botanical-dark/70 backdrop-blur-md px-space-sm py-1 rounded">
                  <Icon
                    name="auto_stories"
                    size={14}
                    className="text-secondary-fixed"
                  />
                  <span className="font-label-sm text-label-sm text-gold-light uppercase tracking-widest">
                    Lead Monograph
                  </span>
                </div>
              </div>

              <div className="lg:col-span-5 p-6 sm:p-8 lg:p-12 flex flex-col justify-between relative z-10 bg-botanical-dark/95 lg:bg-transparent">
                <div>
                  <div className="flex flex-wrap items-center gap-x-space-sm gap-y-1 text-secondary-fixed text-label-sm font-label-sm uppercase tracking-wider mb-4">
                    <span>{lead.topic}</span>
                    <span aria-hidden="true">•</span>
                    <time dateTime={lead.date}>{formatDate(lead.date)}</time>
                    <span aria-hidden="true">•</span>
                    <span className="flex items-center gap-1">
                      <Icon name="schedule" size={13} /> {lead.readMinutes} min
                      read
                    </span>
                  </div>
                  <h2
                    id="lead-essay-title"
                    className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-surface-ivory hover:text-gold-light transition-colors duration-300 !leading-tight"
                  >
                    <AppLink href={`/journal/${lead.slug}`}>
                      {lead.title}
                    </AppLink>
                  </h2>
                  <p className="font-body-md text-body-md text-surface-dim/80 mt-space-md leading-relaxed font-light">
                    {lead.excerpt}
                  </p>
                </div>

                <div className="pt-space-lg mt-space-lg border-t border-border-dark-tint flex flex-wrap items-center justify-between gap-space-md">
                  <div className="flex items-center gap-space-sm">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-primary-container shrink-0">
                      <Picture
                        name="journal-author-portrait"
                        alt=""
                        sizes="40px"
                        className="block w-full h-full"
                        imgClassName="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-body-sm text-body-sm font-semibold text-surface-ivory">
                        {lead.author.name}
                      </div>
                      <div className="font-label-sm text-label-sm text-outline uppercase tracking-wider">
                        {lead.author.role}
                      </div>
                    </div>
                  </div>
                  <AppLink
                    href={`/journal/${lead.slug}`}
                    className="inline-flex items-center gap-space-xs text-gold-light hover:text-white font-label-lg text-label-lg uppercase tracking-wider group/link transition-colors"
                  >
                    <span>
                      Read Essay
                      <span className="sr-only">: {lead.shortTitle}</span>
                    </span>
                    <Icon
                      name="arrow_forward"
                      size={16}
                      className="transition-transform duration-300 group-hover/link:translate-x-1"
                    />
                  </AppLink>
                </div>
              </div>
            </div>
          </article>
        </section>

        {/* FILTERS + GRID */}
        <section
          className={`${container} w-full pb-24`}
          aria-labelledby="all-essays"
        >
          <h2 id="all-essays" className="sr-only">
            All essays
          </h2>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-space-md pb-6 mb-12 border-b border-surface-container-highest">
            <div
              className="flex flex-wrap items-center gap-space-xs"
              role="group"
              aria-label="Filter essays by category"
            >
              {(["all", ...CATEGORIES] as const).map((c) => {
                const active = category === c;
                return (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setCategory(c)}
                    className={`px-4 py-2 rounded font-label-sm text-label-sm uppercase transition-all duration-200 ${
                      active
                        ? "bg-primary text-gold-light shadow-sm"
                        : "bg-surface-cream text-on-surface hover:bg-surface-container-high"
                    }`}
                  >
                    {c === "all" ? "All Essays" : c}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-space-sm w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <label htmlFor={searchId} className="sr-only">
                  Search essays
                </label>
                <input
                  id={searchId}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter topics & monographs..."
                  className="w-full bg-surface-cream font-body-sm text-body-sm text-on-surface placeholder:text-outline pl-9 pr-4 py-2 rounded focus:outline-none focus:ring-1 focus:ring-secondary transition-all"
                />
                <Icon
                  name="search"
                  size={16}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-outline pointer-events-none"
                />
              </div>
            </div>
          </div>

          <p className="sr-only" aria-live="polite">
            {filtering
              ? `${visible.length} ${visible.length === 1 ? "essay" : "essays"} found`
              : ""}
          </p>

          {visible.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
              {visible.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center text-center gap-space-sm py-space-xl px-space-md bg-surface-cream rounded-xl">
              <Icon name="auto_stories" size={32} className="text-secondary" />
              <h3 className="font-headline-sm text-headline-sm text-primary">
                No essays match that search yet
              </h3>
              <p className="font-body-md text-body-md text-on-surface-variant max-w-lg">
                Try another word or category — or ask our designers directly. We
                are always happy to answer questions about your own home.
              </p>
              <div className="flex flex-wrap justify-center gap-space-sm pt-space-xs">
                <button
                  type="button"
                  onClick={clear}
                  className="px-4 py-2 rounded font-label-md text-label-md uppercase tracking-wider bg-surface text-primary hover:bg-surface-container-high transition-colors"
                >
                  Show all essays
                </button>
                <AppLink href="/contact" className={btnPrimary}>
                  Ask a designer <Icon name="arrow_forward" size={16} />
                </AppLink>
              </div>
            </div>
          )}
        </section>

        {/* NEWSLETTER */}
        <section
          className={`${container} w-full pb-24`}
          aria-labelledby="gazette-title"
        >
          <Gazette />
        </section>
      </div>
    </>
  );
}

function Gazette() {
  const { status, error, onSubmit } = useFormSubmit("newsletter");
  const nameId = useId();
  const emailId = useId();
  const done = status === "success";

  return (
    <div className="relative rounded-2xl overflow-hidden bg-primary text-surface-ivory p-6 sm:p-12 lg:p-16 shadow-2xl">
      <div
        className="absolute inset-0 pointer-events-none opacity-10"
        aria-hidden="true"
      >
        <svg
          className="w-full h-full"
          fill="none"
          viewBox="0 0 800 400"
          preserveAspectRatio="xMidYMid slice"
        >
          <line
            stroke="currentColor"
            strokeWidth="1"
            x1="0"
            x2="800"
            y1="100"
            y2="100"
          />
          <line
            stroke="currentColor"
            strokeWidth="1"
            x1="0"
            x2="800"
            y1="200"
            y2="200"
          />
          <line
            stroke="currentColor"
            strokeWidth="1"
            x1="0"
            x2="800"
            y1="300"
            y2="300"
          />
          <circle
            cx="650"
            cy="200"
            r="140"
            stroke="currentColor"
            strokeWidth="1"
          />
        </svg>
      </div>
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-space-sm mb-space-sm">
            <span className="w-6 h-px bg-secondary-fixed" aria-hidden="true" />
            <span className="font-label-sm text-label-sm uppercase tracking-widest text-gold-light">
              Privileged Quarterly Dispatch
            </span>
          </div>
          <h2
            id="gazette-title"
            className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-surface-ivory !leading-tight font-serif"
          >
            The Atelier Gazette
          </h2>
          <p className="font-body-lg text-body-lg text-surface-dim/80 mt-space-sm max-w-xl font-light leading-relaxed">
            Delivered quarterly to architectural patrons, private homeowners,
            and structural designers. No promotional spam, only substantive
            spatial essays, material discoveries, and private project debuts.
          </p>
          <ul className="flex flex-wrap items-center gap-space-md mt-space-lg text-surface-dim text-body-sm font-body-sm">
            <li className="flex items-center gap-2">
              <Icon
                name="verified"
                size={16}
                className="text-secondary-fixed"
              />{" "}
              Curated by Principals
            </li>
            <li className="flex items-center gap-2">
              <Icon
                name="calendar_today"
                size={16}
                className="text-secondary-fixed"
              />{" "}
              4 Issues Annually
            </li>
            <li className="flex items-center gap-2">
              <Icon name="shield" size={16} className="text-secondary-fixed" />{" "}
              Complete Privacy Assured
            </li>
          </ul>
        </div>

        <div className="lg:col-span-5 bg-botanical-dark/60 p-5 sm:p-8 rounded-xl backdrop-blur-md shadow-inner">
          <form
            className="relative flex flex-col gap-4"
            onSubmit={onSubmit}
            hidden={done}
          >
            <Honeypot />
            <div>
              <label
                htmlFor={nameId}
                className="block font-label-sm text-label-sm uppercase tracking-wider text-gold-light mb-1.5"
              >
                Full Name
              </label>
              <input
                id={nameId}
                name="name"
                type="text"
                autoComplete="name"
                required
                placeholder="e.g. Julian Sterling"
                className="w-full bg-surface-ivory/10 text-surface-ivory placeholder:text-surface-dim/40 px-4 py-3 rounded text-body-sm font-body-sm focus:outline-none focus:ring-1 focus:ring-secondary-fixed transition-all"
              />
            </div>
            <div>
              <label
                htmlFor={emailId}
                className="block font-label-sm text-label-sm uppercase tracking-wider text-gold-light mb-1.5"
              >
                Private Email Address
              </label>
              <input
                id={emailId}
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="julian@residence.com"
                className="w-full bg-surface-ivory/10 text-surface-ivory placeholder:text-surface-dim/40 px-4 py-3 rounded text-body-sm font-body-sm focus:outline-none focus:ring-1 focus:ring-secondary-fixed transition-all"
              />
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full bg-gold-dark hover:bg-gold-light hover:text-primary text-surface-ivory font-label-lg text-label-lg uppercase tracking-wider py-3.5 px-6 rounded transition-all duration-300 flex items-center justify-center gap-2 shadow-lg disabled:opacity-60 disabled:cursor-wait"
              >
                <span>
                  {status === "submitting"
                    ? "Sending…"
                    : "Request Membership Invitation"}
                </span>
                <Icon
                  name={status === "submitting" ? "progress_activity" : "east"}
                  size={18}
                  className={status === "submitting" ? "animate-spin" : ""}
                />
              </button>
            </div>
            <FormStatusMessage
              status={done ? "idle" : status}
              error={error}
              successText=""
              tone="dark"
            />
            <p className="font-label-sm text-label-sm text-surface-dim/60 text-center mt-1">
              You may request removal at any point in one click.
            </p>
          </form>

          <div role="status" aria-live="polite">
            {done && (
              <div className="text-center py-8">
                <Icon
                  name="mark_email_read"
                  size={40}
                  className="text-secondary-fixed mb-2 mx-auto"
                />
                <h3 className="font-headline-sm text-headline-sm text-surface-ivory mb-1">
                  Invitation Dispatched
                </h3>
                <p className="font-body-sm text-body-sm text-surface-dim">
                  Thank you. You have been registered for the next issue of the
                  Atelier Gazette.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
